import React from 'react';
import ErrorBoundary from './ErrorBoundary';
import useAsyncData from '../hooks/useAsyncData';
import useStaleWarning from '../hooks/useStaleWarning';
import useAutoRefresh from '../hooks/useAutoRefresh';

const GitActivity = () => {
  const { data: gitData, loading, error, retry } = useAsyncData('/api/git');
  const { isStale, lastUpdated } = useStaleWarning(gitData);
  useAutoRefresh(retry);

  const fmtTime = (d) => d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '–';

  if (loading) {
    return (
      <ErrorBoundary>
        <article className="panel git-panel" aria-busy="true">
          <h2>Git Activity</h2>
          <div className="skeleton">
            <div className="skeleton-line" />
            <div className="skeleton-line" />
          </div>
        </article>
      </ErrorBoundary>
    );
  }

  if (error) {
    return (
      <ErrorBoundary>
        <article className="panel git-panel" aria-live="assertive">
          <h2>Git Activity</h2>
          <p className="error-msg">Failed to load git activity.</p>
          <button type="button" className="btn-retry" onClick={retry}>Retry</button>
        </article>
      </ErrorBoundary>
    );
  }

  if (!gitData) {
    return (
      <ErrorBoundary>
        <article className="panel git-panel">
          <h2>Git Activity</h2>
          <p>No git data available.</p>
        </article>
      </ErrorBoundary>
    );
  }

  const recentCommits = (gitData.recentCommits || []).slice(0, 5);

  return (
    <ErrorBoundary>
      <article className="panel git-panel" aria-label="Git activity" aria-busy="false">
        <div className="panel-header">
          <h2>Recent Commits</h2>
          <div className="panel-meta">
            <span className="last-updated" aria-label="Last updated">
              Last updated: {fmtTime(lastUpdated)}
            </span>
            {isStale && (
              <span className="badge badge-stale" role="status">Stale</span>
            )}
            <button type="button" className="btn-icon btn-refresh" onClick={retry} aria-label="Refresh git">
              ↻
            </button>
          </div>
        </div>
        <ul className="commit-list" aria-label="Recent commits">
          {recentCommits.length === 0 && <li className="empty">No recent commits</li>}
          {recentCommits.map((c, i) => (
            <li key={c.hash || i} className="commit-item">
              <code className="hash">{c.hash}</code>
              <span>{c.message}</span>
            </li>
          ))}
        </ul>
        {gitData.hasUncommitted && <span className="uncommitted-badge">Uncommitted changes</span>}
        <p>Branches: {gitData.branches.join(', ')}</p>
      </article>
    </ErrorBoundary>
  );
};

export default GitActivity;
